/**
 * Analytics constants
 *
 * Centralized constants for analytics data processing.
 */

import { t } from '../../i18n';

import { GRAPH_COLORS } from './colorUtils';

// Valid HRO categories
export const VALID_HROS = ['PF', 'RS', 'SO', 'CR', 'DE', 'NA'];

// Time filter presets
/**
 * Time filter presets, in the order the chips render.
 *
 * Index is the identity — `activeFilter` and TIME_FILTER_CONFIG below are both
 * keyed by position — so the order here must never change, only the copy.
 *
 * Each entry carries its own `short` form rather than having the UI trim a
 * "Past " prefix off the label: that trick only works in English.
 */
export const TIME_FILTER_KEYS = [
  'past24Hours',
  'past7Days',
  'pastMonth',
  'past6Months',
  'pastYear',
  'past3Years',
  'allTime',
];

export function getTimeFilterOptions() {
  return TIME_FILTER_KEYS.map((key) => ({
    label: t(`analytics:timeFilter.${key}.label`),
    short: t(`analytics:timeFilter.${key}.short`),
  }));
}

// Time filter configuration for each preset
// [queryMode, timeAmount, timeStride]
export const TIME_FILTER_CONFIGS = [
  ['hour', 12, 2], // Past 24 hours
  ['day', 7, 1], // Past 7 Days
  ['day', 6, 5], // Past Month (30 days — matches View Reports)
  ['month', 6, 1], // Past 6 months
  ['month', 12, 1], // Past Year
  ['month', 12, 3], // Past 3 Years
  ['month', 12, 3], // All Time (same as 3 years for display)
];

// Default graph state keys
export const GRAPH_STATE_KEYS = ['AF', 'RD', 'AR', 'KPI'];

// Default segments for derived graph info
export const DEFAULT_SEGMENTS = 5;

/**
 * Create empty graph state with placeholder data
 * @returns {Object} Empty graph state
 */
export function createEmptyGraphState() {
  return {
    labels: ['NAN'],
    datasets: [{ data: [0], color: () => GRAPH_COLORS.GRAY }],
  };
}

/**
 * Create default graph states for all chart types
 * @returns {Object} Default graph states keyed by chart type
 */
export function createDefaultGraphStates() {
  return {
    AF: createEmptyGraphState(),
    RD: createEmptyGraphState(),
    AR: createEmptyGraphState(),
    KPI: createEmptyGraphState(),
  };
}

/**
 * Create default derived graph info
 * @returns {Object} Default derived info with segments
 */
export function createDefaultDerivedInfo() {
  return {
    AF: { segments: DEFAULT_SEGMENTS },
    RD: { segments: DEFAULT_SEGMENTS },
    AR: { segments: DEFAULT_SEGMENTS },
    KPI: { segments: DEFAULT_SEGMENTS },
  };
}
